import React from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { useLocalStorage } from '../../Utils/useLocalStorage'

export interface BaseBannerProps {
  /** Unique identifier for the banner to manage its state in localStorage */
  id: string;
  /** Content to display in the banner */
  children: React.ReactNode;
  /** Whether the banner is visible by default */
  isVisible?: boolean;
  /** Custom className for the banner container */
  className?: string;
  /** Custom className for the content container */
  contentClassName?: string;
  /** Custom className for the close button */
  closeButtonClassName?: string;
  /** Callback when the banner is closed */
  onClose?: () => void;
}

/**
 * A reusable banner component that can be used to display any type of notification or message.
 * The banner's state (open/closed) is persisted in localStorage.
 * 
 * @example
 * // Basic usage
 * <BaseBanner id="welcome-banner">
 *   Welcome to our site!
 * </BaseBanner>
 * 
 * @example
 * // With custom styling
 * <BaseBanner 
 *   id="promo-banner"
 *   className="bg-blue-500"
 *   contentClassName="text-white"
 * >
 *   <Link to="/promo">Check out our new promotion!</Link>
 * </BaseBanner>
 */
export const BaseBanner = ({
  id,
  children,
  isVisible = true,
  className = "bg-primary",
  contentClassName = "text-white",
  closeButtonClassName = "text-white",
  onClose,
}: BaseBannerProps) => {
  const storageKey = `banner-${id}-is-open`
  const [isOpen, setIsOpen] = useLocalStorage<boolean>(storageKey, isVisible)

  const handleClose = () => {
    setIsOpen(false)
    onClose?.()
  }

  if (!isOpen || !isVisible) {
    return null
  }

  return (
    <div
      className={`flex items-center gap-x-6 px-6 py-2.5 sm:px-3.5 justify-center transition-all duration-300 ease-in-out ${className}`}
      role="banner"
      aria-label={`Banner ${id}`}
    >
      <div className={`flex items-center justify-between gap-x-6 ${contentClassName}`}>
        {children}
        <button
          type="button"
          className={`flex-none p-3 -m-3 focus-visible:-outline-offset-4 ${closeButtonClassName}`}
          onClick={handleClose}
          aria-label="Fermer"
          title="Fermer"
        >
          <span className="sr-only">Fermer</span>
          <XMarkIcon aria-hidden="true" className="size-5" />
        </button>
      </div>
    </div>
  )
}

